import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Divider,
    Alert,
    CircularProgress,
    Stepper,
    Step,
    StepLabel,
    Chip,
    Autocomplete,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import {
    LocalShipping as ShippingIcon,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Scale as ScaleIcon,
    Receipt as ReceiptIcon,
    CheckCircle as CheckIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { customerBookingService } from '../../services/customerBookingService';
import { useApiHandler } from '../../hooks/useApiHandler';
import { bookingService } from '../../services/bookingService';

const CustomerBookingForm = () => {
    const { handleApiCallWithLoading, showError, showSuccess } = useApiHandler();
    
    // Form state
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [cities, setCities] = useState([]);
    const [products, setProducts] = useState([]);
    const [calculatedRate, setCalculatedRate] = useState(null);
    const [rateLoading, setRateLoading] = useState(false);
    const [boxes, setBoxes] = useState([{ length: '', width: '', height: '', volumetric_weight: 0 }]);
    
    // Form data
    const [formData, setFormData] = useState({
        // Basic details
        booking_date: new Date().toISOString().split('T')[0],
        transport: 'Surface',
        payment_mode: 'BILL TO SHIPPER',
        
        // Origin and destination
        from_city: '',
        from_state: '',
        to_city: '',
        to_state: '',
        
        // Shipper details (customer's details)
        frm_name: '',
        frm_company: '',
        frm_address: '',
        frm_pincode: '',
        frm_tel: '',
        
        // Recipient details
        to_name: '',
        to_company: '',
        to_address: '',
        to_pincode: '',
        to_tel: '',
        
        // Package details
        no_of_pack: 1,
        package_name: '',
        act_wt: '',
        wt_l: '',
        wt_w: '',
        wt_h: '',
        charged_wt: '',
        dec_value: '',
        
        // Optional
        product_id: '',
        special_instructions: ''
    });

    // Load initial data
    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [citiesResponse, productsResponse] = await Promise.all([
                customerBookingService.getCities(),
                customerBookingService.getProducts()
            ]);
            
            if (citiesResponse.success) {
                setCities(citiesResponse.data);
            }
            if (productsResponse.success) {
                setProducts(productsResponse.data);
            }
        } catch (error) {
            showError('Failed to load form data');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleBoxChange = (index, field, value) => {
        if (value !== '' && !/^\d*\.?\d{0,2}$/.test(value)) return;

        setBoxes(prev => {
            const updated = prev.map((box, i) => (i !== index ? box : { ...box, [field]: value }));
            const result = bookingService.calculateMultiBoxWeight(updated, formData.transport || 'Surface');
            const totalVol = result.totalVolumetricWeight;
            const actWt = parseFloat(formData.act_wt) || 0;
            const chargedWt = Math.max(totalVol, actWt);

            setFormData(prev => ({ ...prev, charged_wt: chargedWt, no_of_pack: updated.length }));

            return result.boxes.map((b, i) => ({
                length: updated[i].length,
                width: updated[i].width,
                height: updated[i].height,
                volumetric_weight: b.volumetric_weight,
            }));
        });
    };

    const addBox = () => {
        setBoxes(prev => [...prev, { length: '', width: '', height: '', volumetric_weight: 0 }]);
        setFormData(prev => ({ ...prev, no_of_pack: (prev.no_of_pack || 0) + 1 }));
    };

    const removeBox = (index) => {
        if (boxes.length <= 1) return;
        setBoxes(prev => {
            const updated = prev.filter((_, i) => i !== index);
            const result = bookingService.calculateMultiBoxWeight(updated, formData.transport || 'Surface');
            const totalVol = result.totalVolumetricWeight;
            const actWt = parseFloat(formData.act_wt) || 0;
            setFormData(p => ({ ...p, charged_wt: Math.max(totalVol, actWt), no_of_pack: updated.length }));
            return updated;
        });
    };

    const handleCalculateRate = async () => {
        if (!formData.from_city || !formData.to_city || !formData.charged_wt) {
            showError('Please fill in origin, destination, and weight to calculate rate');
            return;
        }

        setRateLoading(true);
        try {
            const response = await customerBookingService.calculateRate({
                from_city: formData.from_city,
                to_city: formData.to_city,
                weight: parseFloat(formData.charged_wt),
                transport: formData.transport,
                product_id: formData.product_id || null
            });
            
            if (response.success) {
                setCalculatedRate(response.data);
                showSuccess('Rate calculated successfully');
            }
        } catch (error) {
            showError('Failed to calculate rate');
        } finally {
            setRateLoading(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const submitData = { ...formData };
            if (formData.bill_by === 'Dimention' && boxes.length > 0) {
                submitData.boxes = boxes.map(box => ({
                    length: parseFloat(box.length) || 0,
                    width: parseFloat(box.width) || 0,
                    height: parseFloat(box.height) || 0,
                }));
                submitData.no_of_pack = boxes.length;
                submitData.wt_l = 0;
                submitData.wt_w = 0;
                submitData.wt_h = 0;
            }
            const response = await customerBookingService.createBooking(submitData);
            if (response.success) {
                showSuccess('Booking created successfully!');
                // Reset form or redirect
                setFormData({
                    booking_date: new Date().toISOString().split('T')[0],
                    transport: 'Surface',
                    payment_mode: 'BILL TO SHIPPER',
                    from_city: '',
                    from_state: '',
                    to_city: '',
                    to_state: '',
                    frm_name: '',
                    frm_company: '',
                    frm_address: '',
                    frm_pincode: '',
                    frm_tel: '',
                    to_name: '',
                    to_company: '',
                    to_address: '',
                    to_pincode: '',
                    to_tel: '',
                    no_of_pack: 1,
                    package_name: '',
                    act_wt: '',
                    wt_l: '',
                    wt_w: '',
                    wt_h: '',
                    charged_wt: '',
                    dec_value: '',
                    product_id: '',
                    special_instructions: ''
                });
                setCalculatedRate(null);
                setActiveStep(0);
                setBoxes([{ length: '', width: '', height: '', volumetric_weight: 0 }]);
            }
        } catch (error) {
            showError('Failed to create booking');
        } finally {
            setSubmitting(false);
        }
    };

    const steps = [
        'Basic Details',
        'Shipper Information',
        'Recipient Information',
        'Package Details',
        'Review & Submit'
    ];

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Booking Date"
                                type="date"
                                value={formData.booking_date}
                                onChange={(e) => handleChange('booking_date', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <InputLabel>Transport Mode</InputLabel>
                                <Select
                                    value={formData.transport}
                                    onChange={(e) => handleChange('transport', e.target.value)}
                                >
                                    <MenuItem value="Air">Air</MenuItem>
                                    <MenuItem value="Surface">Surface</MenuItem>
                                    <MenuItem value="Sea">Sea</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                options={cities}
                                getOptionLabel={(option) => option.city_name}
                                value={cities.find(city => city.city_name === formData.from_city) || null}
                                onChange={(event, newValue) => {
                                    handleChange('from_city', newValue?.city_name || '');
                                    handleChange('from_state', newValue?.state_name || '');
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Origin City" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                options={cities}
                                getOptionLabel={(option) => option.city_name}
                                value={cities.find(city => city.city_name === formData.to_city) || null}
                                onChange={(event, newValue) => {
                                    handleChange('to_city', newValue?.city_name || '');
                                    handleChange('to_state', newValue?.state_name || '');
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} label="Destination City" required />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Payment Mode</InputLabel>
                                <Select
                                    value={formData.payment_mode}
                                    onChange={(e) => handleChange('payment_mode', e.target.value)}
                                >
                                    <MenuItem value="BILL TO SHIPPER">Bill to Shipper</MenuItem>
                                    <MenuItem value="BILL TO RECEIPIENT">Bill to Recipient</MenuItem>
                                    <MenuItem value="BILL TO THIRD PARTY">Bill to Third Party</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                );

            case 1:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Shipper Information (Your Details)
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={formData.frm_name}
                                onChange={(e) => handleChange('frm_name', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Company/Division"
                                value={formData.frm_company}
                                onChange={(e) => handleChange('frm_company', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Address"
                                multiline
                                rows={3}
                                value={formData.frm_address}
                                onChange={(e) => handleChange('frm_address', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Pincode"
                                value={formData.frm_pincode}
                                onChange={(e) => handleChange('frm_pincode', e.target.value)}
                                inputProps={{ maxLength: 6 }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Contact Number"
                                value={formData.frm_tel}
                                onChange={(e) => handleChange('frm_tel', e.target.value)}
                                required
                            />
                        </Grid>
                    </Grid>
                );

            case 2:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Recipient Information
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Recipient Name"
                                value={formData.to_name}
                                onChange={(e) => handleChange('to_name', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Company/Division"
                                value={formData.to_company}
                                onChange={(e) => handleChange('to_company', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Recipient Address"
                                multiline
                                rows={3}
                                value={formData.to_address}
                                onChange={(e) => handleChange('to_address', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                label="Pincode"
                                value={formData.to_pincode}
                                onChange={(e) => handleChange('to_pincode', e.target.value)}
                                inputProps={{ maxLength: 6 }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Contact Number"
                                value={formData.to_tel}
                                onChange={(e) => handleChange('to_tel', e.target.value)}
                                required
                            />
                        </Grid>
                    </Grid>
                );

            case 3:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Package Details
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Number of Packages"
                                type="number"
                                value={boxes.length}
                                InputProps={{ readOnly: true }}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Package Description"
                                value={formData.package_name}
                                onChange={(e) => handleChange('package_name', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Actual Weight (kg)"
                                type="number"
                                step="0.1"
                                value={formData.act_wt}
                                onChange={(e) => handleChange('act_wt', e.target.value)}
                                required
                            />
                        </Grid>
                        {/* Multi-box dimension rows */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Box Dimensions
                            </Typography>
                            {boxes.map((box, index) => (
                                <Grid container spacing={1} key={index} sx={{ mb: 1 }} alignItems="center">
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={`Box ${index + 1} - L (cm)`}
                                            type="number"
                                            value={box.length}
                                            onChange={(e) => handleBoxChange(index, 'length', e.target.value)}
                                            inputProps={{ min: 0.1, step: 0.1 }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="W (cm)"
                                            type="number"
                                            value={box.width}
                                            onChange={(e) => handleBoxChange(index, 'width', e.target.value)}
                                            inputProps={{ min: 0.1, step: 0.1 }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="H (cm)"
                                            type="number"
                                            value={box.height}
                                            onChange={(e) => handleBoxChange(index, 'height', e.target.value)}
                                            inputProps={{ min: 0.1, step: 0.1 }}
                                            required
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                                            Vol: {box.volumetric_weight} kg
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        {boxes.length > 1 && (
                                            <Button
                                                size="small"
                                                color="error"
                                                onClick={() => removeBox(index)}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>
                            ))}
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={addBox}
                                sx={{ mt: 1 }}
                            >
                                + Add Box
                            </Button>
                        </Grid>

                        {/* Summary */}
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Packages: {boxes.length} &nbsp;|&nbsp;
                                Total Vol. Weight: {boxes.reduce((s, b) => s + (b.volumetric_weight || 0), 0).toFixed(2)} kg &nbsp;|&nbsp;
                                Actual Weight: {parseFloat(formData.act_wt) || 0} kg &nbsp;|&nbsp;
                                <strong>Charged Weight: {formData.charged_wt} kg</strong>
                                {' '}(higher of volumetric vs actual)
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Charged Weight (kg)"
                                type="number"
                                step="0.1"
                                value={formData.charged_wt}
                                onChange={(e) => handleChange('charged_wt', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Declared Value (₹)"
                                type="number"
                                step="0.01"
                                value={formData.dec_value}
                                onChange={(e) => handleChange('dec_value', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Product Type</InputLabel>
                                <Select
                                    value={formData.product_id}
                                    onChange={(e) => handleChange('product_id', e.target.value)}
                                >
                                    <MenuItem value="">Select Product Type</MenuItem>
                                    {products.map((product) => (
                                        <MenuItem key={product.id} value={product.id}>
                                            {product.product_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Special Instructions"
                                multiline
                                rows={2}
                                value={formData.special_instructions}
                                onChange={(e) => handleChange('special_instructions', e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Button
                                variant="contained"
                                onClick={handleCalculateRate}
                                disabled={rateLoading || !formData.from_city || !formData.to_city || !formData.charged_wt}
                                startIcon={rateLoading ? <CircularProgress size={20} /> : <ReceiptIcon />}
                            >
                                {rateLoading ? 'Calculating...' : 'Calculate Rate'}
                            </Button>
                        </Grid>
                        {calculatedRate && (
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Rate Calculation
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">Basic Freight:</Typography>
                                                <Typography variant="h6">₹{calculatedRate.basic_freight}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">Fuel Surcharge:</Typography>
                                                <Typography variant="h6">₹{calculatedRate.fuel_surcharge}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2">Service Tax:</Typography>
                                                <Typography variant="h6">₹{calculatedRate.ser_tax}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="body2" color="primary">Total Amount:</Typography>
                                                <Typography variant="h5" color="primary">₹{calculatedRate.grand_total}</Typography>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                );

            case 4:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>
                                Review Your Booking
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Booking Details</Typography>
                                    <Typography><strong>Date:</strong> {formData.booking_date}</Typography>
                                    <Typography><strong>Transport:</strong> {formData.transport}</Typography>
                                    <Typography><strong>Route:</strong> {formData.from_city} → {formData.to_city}</Typography>
                                    <Typography><strong>Payment:</strong> {formData.payment_mode}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>Package Details</Typography>
                                    <Typography><strong>Packages:</strong> {formData.no_of_pack}</Typography>
                                    <Typography><strong>Weight:</strong> {formData.charged_wt} kg</Typography>
                                    <Typography><strong>Dimensions:</strong> {formData.wt_l}×{formData.wt_w}×{formData.wt_h} cm</Typography>
                                    <Typography><strong>Value:</strong> ₹{formData.dec_value}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        {calculatedRate && (
                            <Grid item xs={12}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Rate Summary</Typography>
                                        <Typography variant="h4" color="primary">₹{calculatedRate.grand_total}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                );

            default:
                return null;
        }
    };

    const handleNext = () => {
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    if (loading) {
        return (
            <DashboardLayout title="Create New Booking" userType="customer">
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout title="Create New Booking" userType="customer">
            <Paper sx={{ p: 3 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <Box sx={{ mb: 4 }}>
                    {renderStepContent(activeStep)}
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Button
                        disabled={activeStep === 0}
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                    <Box>
                        {activeStep < steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting}
                                startIcon={submitting ? <CircularProgress size={20} /> : <CheckIcon />}
                            >
                                {submitting ? 'Creating...' : 'Create Booking'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </DashboardLayout>
    );
};

export default CustomerBookingForm;
